import PasswordField from '@components/v2/form/PasswordField/PasswordField';
import TextField from '@components/v2/form/TextField/TextField';
import { Box, Grid } from '@mui/material';
import { Controller, FormProvider } from 'react-hook-form';
import Link from '../../components/v2/ui/Link/Link';
import Typography from '../../components/v2/ui/Typography/Typography';
import LangEnum from '../../lang/LangEnum';
import { StoreSignUpTestIds } from '../../utils/testIds/storeSignUp.testIds';
import { Team } from '../context/ApplicationContext/applicationContext.types';
import { useAuthContext } from '../login/AuthContext';
import { useTranslation } from '../translation/TranslationContext';
import SignUpTermsNotice from './SignUpTermsNotice';
import { useCreateContextLogin } from './hook/useCreateContextLogin';
import useRegisterStepSubmit from './hooks/useRegisterStepSubmit';
import { CreateAccountProps } from './types/createAccount.types';

export type UserLogin = {
  user?: {
    id: string;
    access_token: string;
    ok: boolean;
    name: string;
    email: string;
    surname: string;
  };
  teams: Array<Team>;
};

const CreateContextLogin = ({ store, isActive, ...rest }: CreateAccountProps) => {
  const { translate } = useTranslation();
  const { loginContext } = useAuthContext();
  const { form, submit, isSubmitting } = useCreateContextLogin({ store });

  const { control } = form;

  const recoverPasswordHref = loginContext?.slug
    ? `/${loginContext.slug}/recover-password`
    : '/recover-password';

  useRegisterStepSubmit({ submit, isSubmitting, isActive });

  return (
    <Box {...rest}>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <Grid container columnSpacing={1} rowSpacing={1.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="username"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.GLOBAL_EMAIL)}
                    slotProps={{
                      htmlInput: { 'test-id': StoreSignUpTestIds.EXISTENT_ACCOUNT_EMAIL },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="password"
                control={control}
                render={({ field, fieldState }) => (
                  <PasswordField
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.GLOBAL_PASSWORD)}
                    slotProps={{
                      htmlInput: { 'test-id': StoreSignUpTestIds.EXISTENT_ACCOUNT_PASSWORD },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="body2" align="right">
                <Link href={recoverPasswordHref}>
                  {translate(LangEnum.LOGIN_I_FORGOT_MY_PASSWORD)}
                </Link>
              </Typography>
            </Grid>
            <Grid size={12} sx={{ mt: 1.5 }}>
              <SignUpTermsNotice />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Box>
  );
};

export default CreateContextLogin;
